import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useApp } from '../App';
import { requireRole } from '../utils/roles';
import { GameRecordTypes, formatDate } from '../utils/constants';

export default function GameRecordPanel() {
  const { currentTeam, isStaff, roles, userProfile } = useApp();
  const canEdit = isStaff;   // 기록 승인·거절 = 운영진 (W1-01)
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [records, setRecords] = useState([]);
  const [tab, setTab] = useState('records');
  const [form, setForm] = useState({ recordType: 'goal', quarter: 1, gameTime: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (currentTeam) loadSchedules(); }, [currentTeam]);

  const loadSchedules = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'teams', currentTeam.id, 'schedules'), orderBy('date', 'desc'));
      const snap = await getDocs(q);
      setSchedules(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadRecords = async (scheduleId) => {
    try {
      const q = query(collection(db, 'gameRecords'), where('scheduleId', '==', scheduleId));
      const snap = await getDocs(q);
      setRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  };

  const handleSelect = (sch) => { setSelectedSchedule(sch); loadRecords(sch.id); };

  const handleAdd = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await addDoc(collection(db, 'gameRecords'), {
        scheduleId: selectedSchedule.id, teamId: currentTeam.id,
        userId: auth.currentUser.uid, userName: userProfile?.name || auth.currentUser.email,
        recordType: form.recordType, quarter: form.quarter,
        gameTime: form.gameTime || null, approvalStatus: 'pending',
        approvedBy: [], isFromWatch: false, createdBy: auth.currentUser.uid,
        createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
      });
      setTab('records'); loadRecords(selectedSchedule.id);
    } catch (err) { alert('등록 실패: ' + err.message); }
    setSaving(false);
  };

  const handleApprove = async (id) => {
    if (!requireRole(roles, 'isStaff')) return;
    try {
      await updateDoc(doc(db, 'gameRecords', id), { approvalStatus: 'approved', approvedBy: [auth.currentUser.uid], updatedAt: Timestamp.now() });
      loadRecords(selectedSchedule.id);
    } catch (err) { alert('승인 실패'); }
  };

  const handleReject = async (id) => {
    if (!requireRole(roles, 'isStaff')) return;
    try {
      await updateDoc(doc(db, 'gameRecords', id), { approvalStatus: 'rejected', rejectedBy: auth.currentUser.uid, updatedAt: Timestamp.now() });
      loadRecords(selectedSchedule.id);
    } catch (err) { alert('거절 실패'); }
  };

  const approved = records.filter(r => r.approvalStatus === 'approved');
  const pending = records.filter(r => r.approvalStatus === 'pending');
  const goals = approved.filter(r => r.recordType === 'goal').length;
  const assists = approved.filter(r => r.recordType === 'assist').length;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, maxWidth: 1200 }}>
      <div style={s.panel}>
        <div style={s.ph}><h2 style={s.pt}>📅 일정 선택</h2></div>
        <div style={s.pb}>
          {loading ? <div style={s.empty}>로딩 중...</div> : schedules.map(sch => (
            <div key={sch.id} onClick={() => handleSelect(sch)} style={{
              padding: '10px 14px', marginBottom: 4, borderRadius: 8, cursor: 'pointer', fontSize: 13,
              backgroundColor: selectedSchedule?.id === sch.id ? 'rgba(91,196,216,0.08)' : 'transparent',
              borderLeft: selectedSchedule?.id === sch.id ? '3px solid #5BC4D8' : '3px solid transparent',
            }}>
              <div style={{ fontWeight: 500 }}>{sch.title}</div>
              <div style={{ fontSize: 11, color: '#778899', marginTop: 2 }}>{formatDate(sch.date)}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={s.panel}>
        <div style={{ ...s.ph, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['records', '경기 기록'], ['pending', '승인 대기 (' + pending.length + ')']].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', fontSize: 12, cursor: 'pointer', fontWeight: 600, backgroundColor: tab === k ? 'rgba(91,196,216,0.15)' : 'transparent', color: tab === k ? '#5BC4D8' : '#667788' }}>{l}</button>
            ))}
          </div>
          {selectedSchedule && <button onClick={() => setTab('add')} style={s.addBtn}>+ 기록 등록</button>}
        </div>
        <div style={s.pb}>
          {!selectedSchedule ? <div style={s.empty}><div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>👈</div>일정을 선택하세요</div>
          : tab === 'add' ? (
            <form onSubmit={handleAdd} style={{ padding: 16, maxWidth: 400 }}>
              <h3 style={{ margin: '0 0 20px', fontSize: 16, color: '#5BC4D8' }}>기록 등록</h3>
              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>기록 유형</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(GameRecordTypes).map(([k, v]) => (
                    <button type="button" key={k} onClick={() => setForm(p => ({ ...p, recordType: k }))} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, backgroundColor: form.recordType === k ? 'rgba(91,196,216,0.15)' : 'rgba(255,255,255,0.03)', color: form.recordType === k ? '#5BC4D8' : '#778899', fontWeight: 600 }}>{v.icon} {v.label}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>쿼터</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[1, 2, 3, 4].map(q => (
                    <button type="button" key={q} onClick={() => setForm(p => ({ ...p, quarter: q }))} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, backgroundColor: form.quarter === q ? 'rgba(91,196,216,0.15)' : 'rgba(255,255,255,0.03)', color: form.quarter === q ? '#5BC4D8' : '#778899', fontWeight: 600 }}>{q === 4 ? 'OT' : q + 'Q'}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={s.label}>경기 시간 (선택)</label>
                <input style={s.input} value={form.gameTime} onChange={e => setForm(p => ({ ...p, gameTime: e.target.value }))} placeholder="예: 12:34" />
              </div>
              <div style={{ fontSize: 12, color: '#F59E0B', marginBottom: 16 }}>⚠️ 경기 종료 후 7일 이내에 등록해야 합니다</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={saving} style={s.submitBtn}>{saving ? '저장 중...' : '등록'}</button>
                <button type="button" onClick={() => setTab('records')} style={s.cancelBtn}>취소</button>
              </div>
            </form>
          ) : tab === 'pending' ? (
            pending.length === 0 ? <div style={s.empty}>승인 대기 중인 기록이 없습니다</div> :
            pending.map(r => (
              <div key={r.id} style={s.rc}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                  <span style={{ fontSize: 24 }}>{GameRecordTypes[r.recordType]?.icon || '🏒'}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{GameRecordTypes[r.recordType]?.label}</div>
                    <div style={{ fontSize: 12, color: '#778899' }}>{r.userName} · {r.quarter}Q {r.gameTime || ''}</div>
                  </div>
                </div>
                {canEdit && <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleReject(r.id)} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', backgroundColor: 'rgba(248,113,113,0.1)', color: '#F87171', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>거절</button>
                  <button onClick={() => handleApprove(r.id)} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', backgroundColor: '#4ADE80', color: '#0A1220', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>승인</button>
                </div>}
              </div>
            ))
          ) : (
            <>
              {records.length > 0 && <div style={{ display: 'flex', gap: 24, padding: '12px 16px', marginBottom: 16, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: 8 }}>
                {[['🏒', goals, '득점', '#F59E0B'], ['👆', assists, '어시스트', '#3B82F6'], ['⭐', goals + assists, '포인트', '#FBBF24']].map(([ic, v, l, c]) => (
                  <div key={l} style={{ textAlign: 'center' }}><div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}><span>{ic}</span><span style={{ fontSize: 22, fontWeight: 700, color: c }}>{v}</span></div><div style={{ fontSize: 11, color: '#778899' }}>{l}</div></div>
                ))}
              </div>}
              {records.length === 0 ? <div style={s.empty}>등록된 경기 기록이 없습니다</div> :
              records.map(r => (
                <div key={r.id} style={s.rc}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                    <span style={{ fontSize: 24 }}>{GameRecordTypes[r.recordType]?.icon || '🏒'}</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{GameRecordTypes[r.recordType]?.label}{r.isFromWatch && <span style={{ marginLeft: 6, fontSize: 11, color: '#4ADE80' }}>⌚</span>}</div>
                      <div style={{ fontSize: 12, color: '#778899' }}>{r.userName || ''} · {r.quarter}Q {r.gameTime || ''}</div>
                    </div>
                  </div>
                  <StatusBadge status={r.approvalStatus} />
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = { approved: ['승인됨', '#4ADE80'], rejected: ['거절됨', '#F87171'], pending: ['대기중', '#FBBF24'] };
  const [label, color] = cfg[status] || cfg.pending;
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 10, color, backgroundColor: color + '1A' }}>{label}</span>;
}

const s = {
  panel: { backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, overflow: 'hidden' },
  ph: { padding: '14px 20px', borderBottom: '1px solid rgba(91,196,216,0.06)' }, pt: { margin: 0, fontSize: 15, fontWeight: 600 },
  pb: { padding: 12, maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#556677', fontSize: 14 },
  addBtn: { padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  label: { fontSize: 12, color: '#8899AA', fontWeight: 600, display: 'block', marginBottom: 8 },
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.1)', fontSize: 14, backgroundColor: 'rgba(10,18,32,0.8)', color: '#E8ECF1', boxSizing: 'border-box' },
  submitBtn: { padding: '10px 24px', borderRadius: 8, border: 'none', backgroundColor: '#5BC4D8', color: '#0A1220', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  cancelBtn: { padding: '10px 20px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#8899AA', fontSize: 13, cursor: 'pointer' },
  rc: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', marginBottom: 6, borderRadius: 8, backgroundColor: 'rgba(255,255,255,0.02)' },
};
